const describeType = (v) => (v === null ? "null" : typeof v);

export default async function fetchPagesInParallel({
  requestPage,
  pageSize = 500,
  concurrency = 6,
  onProgress,
}) {
  const firstResponse = await requestPage({ page: 0, pageSize });
  const { items: firstItems, totalCount } = firstResponse?.data ?? {};

  if (!Number.isFinite(totalCount)) {
    throw new Error(
      `Missing or invalid totalCount in first response (got "${totalCount}") — ` +
        `cannot determine total pages, refusing to silently truncate.`
    );
  }
  if (!Array.isArray(firstItems)) {
    throw new Error(
      `First response missing items[] (got ${describeType(firstItems)}) — refusing to silently drop rows.`
    );
  }

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize));
  const pages = new Array(totalPages);
  pages[0] = firstItems;

  let loaded = 1;
  onProgress?.({ loaded, total: totalPages });

  if (totalPages > 1) {
    let nextPage = 1;
    let aborted = false;

    const worker = async () => {
      while (!aborted) {
        const index = nextPage++;
        if (index >= totalPages) return;
        let response;
        try {
          response = await requestPage({ page: index, pageSize });
        } catch (err) {
          aborted = true;
          throw err;
        }
        if (aborted) return;
        const pageItems = response?.data?.items;
        if (!Array.isArray(pageItems)) {
          aborted = true;
          throw new Error(
            `Page ${index} response missing items[] (got ${describeType(pageItems)}) — refusing to silently drop rows.`
          );
        }
        pages[index] = pageItems;
        loaded++;
        onProgress?.({ loaded, total: totalPages });
      }
    };

    const workerCount = Math.min(concurrency, totalPages - 1);
    await Promise.all(Array.from({ length: workerCount }, worker));
  }

  return pages.flat();
}
